const get = require('lodash/get')
const DiscountPgAdapter = require('./pg-adapter')
const { unitDiscount } = require('./unit-discount')
const { withinDiscountLimit } = require('./discount-limit')

// Tie-break only; the cheapest price for the client always wins first.
const LEVEL_PRECEDENCE = ['order', 'product', 'location', 'market', 'organisation']

// Location, market and organisation items were already matched to the location by
// the query, so they cover every product; product and order items name their target.
const itemCovers = (level, item, productId, orderId) => {
  if (level === 'product') return item.itemId === productId
  if (level === 'order') return item.itemId === orderId
  return true
}

const discountedPrice = (discount, sellPrice) =>
  Math.max(sellPrice - unitDiscount(sellPrice, discount.discountPercentage, discount.discountAmount), 0)

// Set by order.decorateOrderWithResolvedPrices, even when the price is null.
const hasResolvedPrices = order => Object.values(get(order, 'products', {}))
  .every(product => product && 'unitSellPrice' in product)

const pickDiscounts = (discounts, productIds, sellPrices, orderId, currentSellPrices) => {
  const picked = {}
  productIds.forEach(productId => {
    const sellPrice = sellPrices.get(productId)
    // Without a price there is nothing to compare a fixed amount against.
    if (sellPrice === undefined || sellPrice === null) return

    let best = null
    discounts.forEach(discount => {
      const level = discount.application_level
      const item = discount.items.find(candidate => itemCovers(level, candidate, productId, orderId))
      if (!item) return
      // Over the limit discounts are skipped, so the best one within it can still win
      if (!withinDiscountLimit(discount, currentSellPrices.get(productId))) return

      const price = discountedPrice(discount, sellPrice)
      const rank = LEVEL_PRECEDENCE.indexOf(level)
      if (!best || price < best.price || (price === best.price && rank < best.rank)) {
        best = { price, rank, discount, item }
      }
    })

    if (best) {
      const { discountPercentage, discountAmount } = best.discount
      picked[productId] = {
        discountItemId: best.item.id,
        discountPercentage,
        // A percentage discount is turned into its unit amount at this sell price
        discountAmount: discountPercentage !== null
          ? unitDiscount(sellPrice, discountPercentage)
          : discountAmount
      }
    }
  })
  return picked
}

class DiscountResolver {
  constructor (pgConnection) {
    this.pgAdapter = new DiscountPgAdapter(pgConnection)
  }

  /**
   * Picks at most one discount item per product in the order: the one leaving the
   * lowest unit price. Ties go to the more specific level (see LEVEL_PRECEDENCE).
   * Discounts taking more than MAX_DISCOUNT_RATIO off the current sell price are never picked.
   * @param {string} locationId location fsid
   * @param {string} orderId
   * @param {Object} [options]
   * @param {Object} [options.orderDoc] skips fetching the order when already loaded
   * @param {Object} [options.orderApi] fetches and prices the order; needed unless orderDoc is already priced
   * @param {Object} options.priceAdapter reads the current prices the discount limit is measured against
   * @returns {Promise<Object>} the order, each product carrying discountItemId, discountPercentage
   *   and discountAmount (all null when no discount applies)
   */
  async resolveOrderDiscount (locationId, orderId, { orderDoc, orderApi, priceAdapter } = {}) {
    const fetchedOrder = orderDoc || await orderApi.get({ orderId })
    // Discounts are priced off unitSellPrice, so an undecorated order gets its prices first.
    const order = hasResolvedPrices(fetchedOrder)
      ? fetchedOrder
      : await orderApi.decorateOrderWithResolvedPrices(fetchedOrder)
    const productIds = Object.keys(order.products || {})
    if (productIds.length === 0) {
      return order
    }

    // Current prices (not the order's) decide whether a discount stays within MAX_DISCOUNT_RATIO
    const [discounts, { prices: currentPrices }] = await Promise.all([
      this.pgAdapter.getApplicableDiscounts(locationId, order.orderId),
      priceAdapter.getPriceList({ skus: productIds })
    ])
    const sellPrices = new Map(productIds.map(productId => [productId, order.products[productId].unitSellPrice]))
    const currentSellPrices = new Map(currentPrices.map(price => [price.sku, price.sellprice]))
    const picked = pickDiscounts(discounts, productIds, sellPrices, order.orderId, currentSellPrices)

    const products = productIds.reduce((acc, productId) => {
      const discount = picked[productId] || {}
      acc[productId] = {
        ...order.products[productId],
        discountItemId: discount.discountItemId || null,
        discountPercentage: get(discount, 'discountPercentage', null),
        discountAmount: get(discount, 'discountAmount', null)
      }
      return acc
    }, {})

    return {
      ...order,
      products
    }
  }

  /**
   * Picks discounts for a price list the same way resolveOrderDiscount does for an order,
   * using the discounts live now, whatever the list's referenceDate. Without a location the list is returned as is.
   * @param {{referenceDate: string, prices: Object[]}} priceList from priceAdapter.getPriceList
   * Discounts over MAX_DISCOUNT_RATIO of the list's own sell price are never picked.
   * @param {string} locationId location fsid
   * @returns {Promise<{referenceDate: string, prices: Object[]}>} a copy, each price carrying
   *   discountItemId, discountPercentage and discountAmount
   */
  async resolvePriceListDiscount (priceList, locationId) {
    const productIds = get(priceList, 'prices', []).map(price => price.sku)
    if (!locationId || productIds.length === 0) {
      return priceList
    }

    const discounts = await this.pgAdapter.getApplicableDiscounts(locationId, null)
    const sellPrices = new Map(priceList.prices.map(price => [price.sku, price.sellprice]))
    const picked = pickDiscounts(discounts, productIds, sellPrices, null, sellPrices)

    return {
      ...priceList,
      prices: priceList.prices.map(price => {
        const discount = picked[price.sku] || {}
        return {
          ...price,
          discountItemId: discount.discountItemId || null,
          discountPercentage: get(discount, 'discountPercentage', null),
          discountAmount: get(discount, 'discountAmount', null)
        }
      })
    }
  }
}

module.exports = DiscountResolver
